import { useEffect, useMemo, useState } from "react"
import {
  HeaderBackButton,
  HeaderBackButtonInner,
  UpdateGoalContent,
  UpdateGoalHeader,
  UpdateGoalWrapper,
} from "./styled"
import UpdateBody from "./UpdateBody"
import { useSetPageTitle } from "libs/ga"
import ChevronLeftBold from "components/Icons/ChevronLefBold"
import Stepper from "components/onboarding/Stepper"
import {
  IActivityLevel,
  IGoalTypeName,
  ISetGoalPayload,
} from "features/onboarding/types"
import Button from "components/Buttons/Button"
import SetTypeName from "features/onboarding/SetGoal/Steps/SetTypeName"
import SetTargetWeight from "features/onboarding/SetGoal/Steps/SetTargetWeight"
import SetActivityLevel from "features/onboarding/SetGoal/Steps/SetActivityLevel"
import {
  getBodyWeightValueFromGram,
  getGramWeight,
  getMaxBodyWeight,
  getMinBodyWeight,
  kgToPound,
  poundToKg,
} from "utils/weightConverter"
import { MAINTAIN_WEIGHT_TYPES } from "config"
import {
  useBody,
  useBodyDisplayUnits,
  useLatestUserScan,
} from "features/profile/hooks/body"
import {
  IBody,
  IBodyDisplayUnits,
  IChangeBodyAndGoalPayload,
} from "features/profile/profileTypes"
import useChangeGoal from "features/profile/hooks/changeGoal"
import AppStyleCtaBox from "components/Common/AppStyleCtaBox"
import { useNavigate } from "react-router-dom"
import PATHS from "router/paths"

enum IUpdateBodyAndGoalStep {
  UpdateBody,
  SetActivityLevel,
  SetTypeName,
  SetTargetWeight,
}

const UpdateBodyAndGoal = () => {
  useSetPageTitle("Update body and goal | Nutrition Target")

  const navigate = useNavigate()

  const { bodyDisplayUnits: initBodyDisplayUnits, getBodyDisplayUnitsLoading } =
    useBodyDisplayUnits()
  const [bodyDisplayUnits, setBodyDisplayUnits] = useState(initBodyDisplayUnits)
  const { body: initBody, getBodyLoading } = useBody()
  useLatestUserScan() // Fetch latest scan once when entering flow
  const [body, setBody] = useState<IBody>(initBody)
  const [activityLevel, setActivityLevel] = useState<IActivityLevel>("ACTIVE")
  const [typeName, setTypeName] = useState<IGoalTypeName>("WEIGHT_LOSS")
  const [weightChangePerWeek, setWeightChangePerWeek] = useState(0.5)
  const [targetWeight, setTargetWeight] = useState(
    getBodyWeightValueFromGram(initBodyDisplayUnits, initBody.weight),
  )

  useEffect(() => {
    if (initBody) {
      setBody(initBody)
    }
  }, [initBody])

  const currentBodyWeight = body.weight

  const currentWeightInDisplayUnit = getBodyWeightValueFromGram(
    bodyDisplayUnits,
    currentBodyWeight,
  )

  useEffect(() => {
    setTargetWeight(
      getBodyWeightValueFromGram(bodyDisplayUnits, currentBodyWeight),
    )
  }, [currentBodyWeight, bodyDisplayUnits])

  useEffect(() => {
    setBodyDisplayUnits(initBodyDisplayUnits)
  }, [initBodyDisplayUnits])

  const onBodyDisplayUnitsChange = (
    nextBodyDisplayUnits: IBodyDisplayUnits,
  ) => {
    const nextTargetWeight =
      nextBodyDisplayUnits === "IMPERIAL"
        ? kgToPound(targetWeight)
        : poundToKg(targetWeight)
    setBodyDisplayUnits(nextBodyDisplayUnits)
    setTargetWeight(nextTargetWeight)
  }

  const { handleChangeGoal, changeGoalLoading } = useChangeGoal(() => {
    navigate(PATHS.app.yourGoal.root)
  })

  const onTargetWeightChange = (nextTargetWeight: number) => {
    setTargetWeight(nextTargetWeight)
  }

  const hiddenGoalTypes = useMemo(() => {
    const hidden: IGoalTypeName[] = []
    const minBodyWeight = getMinBodyWeight(bodyDisplayUnits)
    const maxBodyWeight = getMaxBodyWeight(bodyDisplayUnits)

    if (currentWeightInDisplayUnit <= minBodyWeight) {
      hidden.push("WEIGHT_LOSS")
    }
    if (currentWeightInDisplayUnit >= maxBodyWeight) {
      hidden.push("BUILDING")
    }

    return hidden
  }, [bodyDisplayUnits, currentWeightInDisplayUnit])

  useEffect(() => {
    if (hiddenGoalTypes.includes(typeName)) {
      const defaultType = hiddenGoalTypes.includes("WEIGHT_LOSS")
        ? "BUILDING"
        : "WEIGHT_LOSS"
      setTypeName(defaultType)
    }
  }, [hiddenGoalTypes])

  const getPayload = (isMaintain?: boolean) => {
    let changeGoalPayload: ISetGoalPayload = {
      activityLevel,
      typeName,
      height: body.height,
      weight: body.weight,
    }

    if (!isMaintain) {
      const gramTargetWeight = getGramWeight(bodyDisplayUnits, targetWeight)
      const gramWeightChangePerWeek = getGramWeight(
        bodyDisplayUnits,
        weightChangePerWeek,
      )

      changeGoalPayload = {
        ...changeGoalPayload,
        weightChangePerWeek: gramWeightChangePerWeek,
        targetWeight: gramTargetWeight,
      }
    }

    const payload: IChangeBodyAndGoalPayload = {
      changeGoalPayload,
    }

    const isBodyDisplayUnitsChanged = bodyDisplayUnits !== initBodyDisplayUnits

    if (isBodyDisplayUnitsChanged) {
      payload.bodyDisplayUnits = bodyDisplayUnits
    }

    return payload
  }

  const goNext = () => {
    switch (step) {
      case IUpdateBodyAndGoalStep.SetActivityLevel:
        setStep(IUpdateBodyAndGoalStep.SetTypeName)
        break

      case IUpdateBodyAndGoalStep.SetTypeName:
        const isMaintain = MAINTAIN_WEIGHT_TYPES.includes(typeName)
        if (isMaintain) {
          handleChangeGoal(getPayload(isMaintain))
        } else {
          setStep(IUpdateBodyAndGoalStep.SetTargetWeight)
        }
        break

      case IUpdateBodyAndGoalStep.SetTargetWeight:
        handleChangeGoal(getPayload())
        break

      default:
        break
    }
  }

  const [step, setStep] = useState(IUpdateBodyAndGoalStep.UpdateBody)

  if (step === IUpdateBodyAndGoalStep.UpdateBody) {
    return (
      <UpdateBody
        getBodyLoading={getBodyLoading}
        body={body}
        getBodyDisplayUnitsLoading={getBodyDisplayUnitsLoading}
        bodyDisplayUnits={bodyDisplayUnits}
        setBodyDisplayUnits={onBodyDisplayUnitsChange}
        goNextStep={(body: IBody) => {
          setBody(body)
          setStep(IUpdateBodyAndGoalStep.SetActivityLevel)
        }}
      />
    )
  }

  const onBack = () => {
    if (changeGoalLoading) {
      return
    }

    setStep((step) => {
      return step - 1
    })
  }

  const isDisableGoNextStep =
    step === IUpdateBodyAndGoalStep.SetTargetWeight &&
    targetWeight === currentWeightInDisplayUnit

  return (
    <UpdateGoalWrapper>
      <UpdateGoalHeader>
        <HeaderBackButton onClick={onBack}>
          <HeaderBackButtonInner $isDisabled={changeGoalLoading}>
            <ChevronLeftBold fill={"#4BD9DE"} isDisabled={changeGoalLoading} />
          </HeaderBackButtonInner>
        </HeaderBackButton>

        <Stepper currentStep={step - 1} />
      </UpdateGoalHeader>

      <UpdateGoalContent>
        {step === IUpdateBodyAndGoalStep.SetActivityLevel && (
          <SetActivityLevel value={activityLevel} onChange={setActivityLevel} />
        )}

        {step === IUpdateBodyAndGoalStep.SetTypeName && (
          <SetTypeName
            onChange={setTypeName}
            value={typeName}
            hiddenGoalTypes={hiddenGoalTypes}
          />
        )}

        {step === IUpdateBodyAndGoalStep.SetTargetWeight && (
          <SetTargetWeight
            weightChangePerWeek={weightChangePerWeek}
            onWeightChangePerWeekChange={setWeightChangePerWeek}
            targetWeight={targetWeight}
            onTargetWeightChange={onTargetWeightChange}
            typeName={typeName as "WEIGHT_LOSS" | "BUILDING"}
            bodyDisplayUnits={bodyDisplayUnits}
            currentWeightInGrams={body.weight}
            disabled={changeGoalLoading}
          />
        )}
      </UpdateGoalContent>

      <AppStyleCtaBox>
        <Button
          $type="primary"
          style={{ width: "100%" }}
          loading={changeGoalLoading}
          onClick={goNext}
          disabled={isDisableGoNextStep}
          $theme="dark"
          $borderLess
        >
          Continue
        </Button>
      </AppStyleCtaBox>
    </UpdateGoalWrapper>
  )
}

export default UpdateBodyAndGoal
